/* eslint-disable restrict-imports/restrict-imports */
import React, { useState, useMemo } from 'react';
import { Button, Icon, Table, TableColumn, Typography } from '@scale/scaleui';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { twMerge } from 'tailwind-merge';
import { LoadingTable } from './LoadingTable';
import { IWorkerManagerEvent, WorkerManagerEventType } from '@scale/shared/types/expertManagement';
import useFetchTeamChangeLog from '../hooks/queries/useFetchTeamChangeLog';
import { Link } from 'react-router-dom';
import { Routes, buildRoute } from 'react-scale/components/ExpertManagement/urls';
import { formatName } from './TeamLeadOverview/TeamLeadOverview';
import _ from 'lodash';

const EVENT_TYPE_TO_COLORS: Record<string, string> = {
  [WorkerManagerEventType.RemoveManager]: 'bg-danger-300',
  'transferred out of': 'bg-danger-100',
  'transferred in to': 'bg-success-100',
  [WorkerManagerEventType.ChangeManager]: 'bg-warning-200',
  [WorkerManagerEventType.AddManager]: 'bg-success-300',
};

const EVENT_TYPE_TO_ICON: Record<WorkerManagerEventType, IconName> = {
  [WorkerManagerEventType.RemoveManager]: 'circle-minus',
  [WorkerManagerEventType.ChangeManager]: 'triangle',
  [WorkerManagerEventType.AddManager]: 'circle-plus',
};

interface IUserInfo {
  _id: string;
  firstName: string;
}

const UserEventCell: React.FC<{ user: IUserInfo }> = ({ user }) => {
  return (
    <span>
      <Link to={buildRoute(Routes.ExpertManagement, { user: user._id })} className="flex flex-col">
        <div>{!user?.firstName ? 'No Name Found' : formatName(user)}</div>
        <div className="text-xs">{user._id}</div>
      </Link>
    </span>
  );
};

const UserNameDisplay: React.FC<{ user: IUserInfo }> = ({ user }) => {
  if (!user?.firstName) return <div className="font-normal w-fit">• Unnamed User</div>;
  return <div className="font-normal w-fit">• {formatName(user)}</div>;
};

const ManagerSummaryDataColumns: TableColumn[] = [
  {
    id: 'timestamp',
    header: 'Timestamp',
    cellClassName: 'flex flex-col justify-start gap-1',
    cell: function Timestamp(props: any) {
      return (
        <>
          {new Date(props.row.original.timestamp)
            .toLocaleString()
            .split(',')
            .map((s: string, i: number) => (
              <div key={`${s}-${i}`}>{s}</div>
            ))}
        </>
      );
    },
  },
  {
    id: 'summary',
    header: 'Summary',
    enableSorting: false,
    cellClassName: 'align-top',
    cell: function Summary(props: any) {
      const events = props.row.original.events;
      const numAdd = events.filter((e: any) => e.type === WorkerManagerEventType.AddManager).length;
      const numRemove = events.filter(
        (e: any) => e.type === WorkerManagerEventType.RemoveManager,
      ).length;
      const numChangeAdd = events.filter(
        (e: any) =>
          e.type === WorkerManagerEventType.ChangeManager &&
          e.toManager._id === props.row.original.managerId,
      ).length;
      const numChangeRemove = events.filter(
        (e: any) =>
          e.type === WorkerManagerEventType.ChangeManager &&
          e.fromManager._id === props.row.original.managerId &&
          e.toManager._id !== props.row.original.managerId,
      ).length;
      const previousManagers: IUserInfo[] = _.uniqBy(
        events.map((e: any) => e.fromManager),
        '_id',
      ) as unknown as IUserInfo[];
      const newManagers: IUserInfo[] = _.uniqBy(
        events.map((e: any) => e.toManager),
        '_id',
      ) as unknown as IUserInfo[];
      return (
        <div className="flex flex-col justify-start gap-1">
          {numAdd > 0 && (
            <div className="flex items-center">
              <Icon
                icon={['far', EVENT_TYPE_TO_ICON[WorkerManagerEventType.AddManager]]}
                rounded
                size="sm"
                className="flex-shrink-0"
              />
              <span>{numAdd} Team Members Added</span>
            </div>
          )}
          {numRemove > 0 && (
            <div className="flex items-center">
              <Icon
                icon={['far', EVENT_TYPE_TO_ICON[WorkerManagerEventType.RemoveManager]]}
                rounded
                size="sm"
                className="flex-shrink-0"
              />
              <span>{numRemove} Team Members Removed</span>
            </div>
          )}
          {numChangeAdd > 0 && (
            <div className="flex items-center">
              <Icon
                icon={['far', EVENT_TYPE_TO_ICON[WorkerManagerEventType.ChangeManager]]}
                rounded
                size="sm"
                className="flex-shrink-0"
              />
              <span>{numChangeAdd} Team Members Transferred In</span>
            </div>
          )}
          {numChangeRemove > 0 && (
            <div className="flex items-center">
              <Icon
                icon={['far', EVENT_TYPE_TO_ICON[WorkerManagerEventType.ChangeManager]]}
                rounded
                size="sm"
                className="flex-shrink-0"
              />
              <span>{numChangeRemove} Team Members Transferred Out</span>
            </div>
          )}
          {numChangeAdd > 0 && (
            <div className="flex flex-col w-full">
              <span className="ml-1">Moved from Manager(s):</span>
              <div className="flex flex-col justify-end items-end">
                {previousManagers.map((previousManager: IUserInfo, i: number) => (
                  <UserNameDisplay
                    key={`numChangeAdd-${previousManager._id}-${i}`}
                    user={previousManager}
                  />
                ))}
              </div>
            </div>
          )}
          {numChangeRemove > 0 && (
            <div className="flex flex-col w-full">
              <span className="ml-1">Moved to Manager(s):</span>
              <div className="flex flex-col justify-end items-end">
                {newManagers.map((newManager: IUserInfo, i: number) => (
                  <UserNameDisplay
                    key={`numChangeRemove-${newManager._id}-${i}`}
                    user={newManager}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      );
    },
  },
  {
    id: 'worker',
    cellClassName: 'p-0 flex flex-col justify-start',
    enableSorting: false,
    // @ts-ignore: render custom header
    header: function EventHeader(_props: any) {
      return (
        <div className={'flex gap-1'}>
          <div className="py-2 font-bold w-[260px]">Tasker</div>
          <div className="py-2 font-bold w-[260px]">Previous Reviewer</div>
          <div className="py-2 font-bold w-[260px]">New Reviewer</div>
          <div className="py-2 font-bold w-[280px]">Performed By</div>
        </div>
      );
    },
    cell: function Event(props: any) {
      const [showAll, setShowAll] = useState<boolean>(false);
      let canShowAll = false;
      if (props.row.original.events.length > 3) canShowAll = true;
      return (
        <div className="flex flex-col gap-2">
          {(showAll ? props.row.original.events : props.row.original.events.slice(0, 3)).map(
            (event: IWorkerManagerEvent, i: number) => {
              let eventType: string = event.type;
              if (event.type === WorkerManagerEventType.ChangeManager)
                eventType =
                  (event.toManager as unknown as IUserInfo)._id === props.row.original.managerId
                    ? 'transferred in to'
                    : (event.fromManager as unknown as IUserInfo)._id ===
                      props.row.original.managerId
                    ? 'transferred out of'
                    : WorkerManagerEventType.ChangeManager;
              return (
                <div key={`${event._id}-${i}`} className="flex gap-2 px-2">
                  <div className="flex gap-2 w-[260px] overflow-hidden justify-start font-medium">
                    <Icon
                      icon={['far', EVENT_TYPE_TO_ICON[event.type]]}
                      rounded
                      size="sm"
                      className={twMerge('flex-shrink-0 mt-1', EVENT_TYPE_TO_COLORS[eventType])}
                      hasBackground
                      tooltip={`This user was ${
                        eventType === WorkerManagerEventType.AddManager
                          ? 'added to'
                          : eventType === WorkerManagerEventType.RemoveManager
                          ? 'removed from'
                          : eventType
                      } this team`}
                    />
                    {event.worker ? (
                      <UserEventCell user={event.worker as unknown as IUserInfo} />
                    ) : (
                      'N/A'
                    )}
                  </div>
                  <div className="flex gap-2 w-[260px] overflow-hidden font-medium">
                    {event.fromManager ? (
                      <UserEventCell user={event.fromManager as unknown as IUserInfo} />
                    ) : (
                      'N/A'
                    )}
                  </div>
                  <div className="flex gap-2 w-[260px] overflow-hidden font-medium">
                    {event.toManager ? (
                      <UserEventCell user={event.toManager as unknown as IUserInfo} />
                    ) : (
                      'N/A'
                    )}
                  </div>
                  <div className="flex gap-2 w-[280px] overflow-hidden font-medium">
                    {event.performedBy ? (
                      <UserEventCell user={event.performedBy as unknown as IUserInfo} />
                    ) : (
                      'N/A'
                    )}
                  </div>
                </div>
              );
            },
          )}
          {canShowAll && !showAll && (
            <div className="flex w-full justify-center">
              <Button variant="neutral" className="m-1" onClick={() => setShowAll(true)}>
                Show All
              </Button>
            </div>
          )}
        </div>
      );
    },
  },
];

interface ChangeLogTableProps {
  managerId: string;
  title?: string;
}

const ChangeLogTable: React.FC<ChangeLogTableProps> = ({
  managerId,
  title = 'Team Change Log',
}: ChangeLogTableProps) => {
  const { events: ev, loading } = useFetchTeamChangeLog(managerId);
  const events = useMemo(() => ev.map((e: any) => ({ ...e, managerId })), [ev, managerId]);
  return (
    <>
      <div className="flex flex-col gap-4 w-fit">
        <div className="flex items-center gap-4">
          <Typography.Subtitle2>{title}</Typography.Subtitle2>
        </div>
        {loading ? (
          <LoadingTable columns={ManagerSummaryDataColumns} rowHeight={2} numRows={2} />
        ) : (
          <Table
            className="w-fit"
            columns={ManagerSummaryDataColumns}
            data={events}
            defaultPageSize={10}
          />
        )}
      </div>
    </>
  );
};

export default ChangeLogTable;
